<!--
  InstallInChromeSheet — «ثبّت من كروم» for Samsung Internet and the other
  Android browsers (usePwaInstall rule 7; pack prompt 7 Part B, owner research
  2026-11-03).

  Why: Samsung Internet's installs have shown Play Protect's "blocked
  dangerous app" on Android 14+ since April 2026, and Mi, Oppo / vivo,
  Phoenix, Opera, Firefox and UC make a browser-badged shortcut at best. The
  install card's own "how to install here" instructions would steer the
  member into that. Chrome installs the real app, and a signed-in member can
  mint a launch token, so the one button opens Chrome ALREADY SIGNED IN on
  /launch?t=…, where the install card follows.

  The link is a real anchor to the Chrome intent URL
  (intent://…/launch?t=…#Intent;scheme=https;package=com.android.chrome;
  S.browser_fallback_url=…;end): Chrome's intent rules want a user tap on a
  link, and on a phone without Chrome the fallback — the same /launch?t= link
  — opens in this browser and lands him where he was, signed in. The token is
  minted when the sheet opens and minted AGAIN on a timer while it stays open
  (a launch token lives 60 s), so the anchor is always fresh and the tap is
  always a plain link navigation — never a mint after the tap, which could
  outlive Chrome's transient activation and be refused silently. Minting is
  bounded (launchTarget.mintLaunchToken).

  Presentational: it never decides to open. usePwaInstall's presenter sets
  `opened` (through the scheduler's install slot, or a profile-row tap) and
  reads the `closed` outcome: 'handoff' (the link was tapped — reported at
  once, before the navigation can unload the tab), 'dismissed' («بعدين», the
  backdrop) or 'unshown' (the link never got ready, or the sheet was taken
  back: no decision of his). Mounted at App.vue root, over any route; it
  tells usePwaInstall when it is in the tree. Design family: the two coaches.
-->
<template>
  <!--
    No swipe-to-close: the content scrolls in .sheet-modal-inner, which F7's
    swipe guard does not read (it only spares a scrolled .page-content), so a
    scroll on a short screen would drag the sheet shut. «بعدين» and the
    backdrop close it.
  -->
  <f7-sheet
    v-if="isVisible"
    backdrop
    :opened="open"
    :swipe-to-close="false"
    class="install-chrome-sheet sheet-scroll sheet-scroll-inner"
    @sheet:closed="onClosed"
  >
    <div class="install-chrome-sheet__content">
      <div class="install-chrome-sheet__visual">
        <f7-icon
          f7="globe"
          size="34px"
          color="purple"
        ></f7-icon>
        <f7-icon
          f7="arrow_right"
          size="18px"
          color="gray"
          class="install-chrome-sheet__arrow"
        ></f7-icon>
        <f7-icon
          f7="square_arrow_down"
          size="34px"
          color="purple"
        ></f7-icon>
      </div>

      <h2 class="install-chrome-sheet__title">
        {{ $t('installInChrome.title') }}
      </h2>
      <p class="install-chrome-sheet__body">
        {{ $t('installInChrome.body') }}
      </p>

      <p
        v-if="state === 'error'"
        class="install-chrome-sheet__error"
        role="alert"
      >
        {{ $t('installInChrome.error') }}
      </p>

      <div class="install-chrome-sheet__actions">
        <!--
          `external` is load-bearing: F7's click handler would otherwise feed
          the intent:// href to the router as a relative path.
        -->
        <a
          v-if="state === 'ready'"
          class="external button button-large button-fill color-purple install-chrome-sheet__action"
          :href="intentUrl"
          @click="onTap"
        >
          {{ $t('installInChrome.action') }}
        </a>
        <f7-button
          v-else-if="state === 'error'"
          large
          fill
          color="purple"
          class="install-chrome-sheet__action"
          @click="mint"
        >
          {{ $t('installInChrome.action') }}
        </f7-button>
        <f7-button
          v-else
          large
          fill
          color="purple"
          class="install-chrome-sheet__action"
          disabled
        >
          <f7-preloader size="18px" class="install-chrome-sheet__preloader"></f7-preloader>
          {{ $t('installInChrome.preparing') }}
        </f7-button>
        <f7-button
          class="install-chrome-sheet__dismiss"
          @click="dismiss"
        >
          {{ $t('installInChrome.dismiss') }}
        </f7-button>
      </div>
    </div>
  </f7-sheet>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePwaInstall } from '@/composables/usePwaInstall.js';
import { buildChromeIntentUrl, mintLaunchToken } from '@/utils/launchTarget.js';

const props = defineProps({
  // Set by App.vue while usePwaInstall's presenter wants it open.
  opened: { type: Boolean, default: false },
});

const emit = defineEmits(['closed']);

// The link is minted again this long before the token's lifetime ends, and
// at least this often: the tap must always find a token Chrome can redeem.
const REFRESH_MARGIN_MS = 15_000;
const MAX_REFRESH_MS = 40_000;
const MIN_REFRESH_MS = 5_000;

const { setChromeHandoffMounted } = usePwaInstall();

const isVisible = ref(false);
const open = ref(false);
// 'preparing' | 'ready' | 'error'
const state = ref('preparing');
const intentUrl = ref('');
let outcome = 'dismissed';
let reported = false;
let minted = false;
let mintSeq = 0;
let refreshTimer = null;

function stopRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = null;
}

function scheduleRefresh(expiresIn) {
  stopRefresh();
  const ms = Math.max(MIN_REFRESH_MS, Math.min(MAX_REFRESH_MS, expiresIn * 1000 - REFRESH_MARGIN_MS));
  refreshTimer = setTimeout(mint, ms);
}

async function mint() {
  const seq = ++mintSeq;
  // A background refresh keeps the current link on screen until the new one.
  if (!intentUrl.value) state.value = 'preparing';
  try {
    const { token, expiresIn } = await mintLaunchToken();
    if (seq !== mintSeq || !isVisible.value) return;
    intentUrl.value = buildChromeIntentUrl(token);
    minted = true;
    state.value = 'ready';
    scheduleRefresh(expiresIn);
  } catch (err) {
    if (seq !== mintSeq || !isVisible.value) return;
    stopRefresh();
    // His session is gone: the interceptor is already sending him to the
    // login screen; the sheet must not stay over it.
    if (err?.status === 401) {
      closeWith('unshown');
      return;
    }
    // A refresh that failed leaves the last link up; a first mint shows the error.
    if (!intentUrl.value) state.value = 'error';
  }
}

/**
 * Report the close once. 'handoff' is reported ON the tap, before the
 * navigation: on a phone without Chrome the intent's fallback unloads this
 * tab, and a report that waited for the close animation would never arrive
 * (no snooze, the same sheet again at the next boot).
 */
function report(result) {
  if (reported) return;
  reported = true;
  emit('closed', result);
}

function closeWith(result) {
  outcome = result;
  // Closed before it ever opened (a mint that failed inside the opening
  // tick): F7 will emit no close, so finish here.
  if (!open.value) {
    onClosed();
    return;
  }
  open.value = false;
}

// The anchor navigates on its own.
function onTap() {
  stopRefresh();
  outcome = 'handoff';
  report('handoff');
  open.value = false;
}

function dismiss() {
  closeWith('dismissed');
}

function onClosed() {
  stopRefresh();
  isVisible.value = false;
  // F7 closed it itself on a backdrop tap: `open` must follow, or the next
  // show() would mount a sheet that is already "open" and skip its animation.
  open.value = false;
  // A dismissal before the link ever got ready was no decision of his.
  const result = outcome === 'dismissed' && !minted ? 'unshown' : outcome;
  outcome = 'dismissed';
  report(result);
}

function show() {
  outcome = 'dismissed';
  reported = false;
  minted = false;
  intentUrl.value = '';
  state.value = 'preparing';
  isVisible.value = true;
  mint();
  // Open after the v-if mount, so the sheet animates in — unless it was
  // already finished within the tick (a 401 on the mint).
  nextTick(() => {
    if (props.opened && isVisible.value) open.value = true;
  });
}

onMounted(() => setChromeHandoffMounted(true));

// Unmounted while open (the root re-rendering): report the close, or the
// presenter would keep waiting for it.
onBeforeUnmount(() => {
  stopRefresh();
  if (isVisible.value) report(outcome === 'handoff' ? 'handoff' : 'unshown');
  setChromeHandoffMounted(false);
});

watch(
  () => props.opened,
  (wanted) => {
    if (wanted) {
      show();
      return;
    }
    // Taken back from outside (a sign-out) while still open: no decision of his.
    if (open.value && outcome !== 'handoff') outcome = 'unshown';
    open.value = false;
  },
  { immediate: true },
);
</script>

<style scoped>
.install-chrome-sheet {
  --f7-sheet-bg-color: #ffffff;
  --f7-sheet-border-color: transparent;
  height: auto;
  max-height: 90%;
}

.install-chrome-sheet__content {
  padding:
    24px
    max(20px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: start;
}

.install-chrome-sheet__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

/* Mirrored in RTL by the non-scoped block below (the compound :global() form
   is forbidden inside <style scoped>, see CLAUDE.md). */
.install-chrome-sheet__arrow {
  transform: scaleX(1);
}

.install-chrome-sheet__title {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
}

.install-chrome-sheet__body {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #444444;
}

.install-chrome-sheet__error {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--poolit-error-700, #b91c1c);
  text-align: center;
}

.install-chrome-sheet__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.install-chrome-sheet__action {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
}

.install-chrome-sheet__preloader {
  --f7-preloader-color: #ffffff;
}

.install-chrome-sheet__dismiss {
  font-size: 15px;
  color: #666666;
}
</style>

<!-- Rules that must reach [dir='rtl'] on <html>: non-scoped on purpose. -->
<style>
[dir='rtl'] .install-chrome-sheet__arrow {
  transform: scaleX(-1);
}
</style>
